import React, { Component } from 'react';
import { Row, Col } from 'reactstrap';
import { injectIntl, intlShape } from 'react-intl';
import { object, string } from 'prop-types';
import IframeConstructorPreview from './IframeConstructorPreview';
import IframeConstructorCommonControls from './IframeConstructorCommonControls';
import IframeConstructorFieldsControls from './IframeConstructorFieldsControls';
import IframeConstructorButtonControls from './IframeConstructorButtonControls';
import IframeConstructorControlsAdditional from './IframeConstructorControlsAdditional';
import IframeConstructorCode from './IframeConstructorCode';
import { config as rawConfig } from './iframeConstructorConfig';
import { formatNumberValue, getFormattedColor } from './iframeConstructorHelpers';

class IframeConstructor extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    settings: string,
    link: string,
  };

  static defaultProps = {
    settings: null,
  };

  state = {
    settings: {
      font: {
        family: 'Arial, Helvetica, sans-serif',
        size: 18,
      },
      form: {
        bgColor: '#FFFFFF',
        textColor: '#333333',
        additionalTextColor: '#737373',
        maxWidth: 600,
        enabledMaxWidth: false,
      },
      field: {
        bgColor: '#FFFFFF',
        textColor: '#333333',
        height: 64,
        maxWidth: 536,
        enabledMaxWidth: false,
      },
      button: {
        bgColor: '#E6084B',
        textColor: '#FFFFFF',
        text: 'Оформить заказ',
        textSize: 24,
        height: 80,
        borderRadius: 40,
        maxWidth: 300,
        enabledMaxWidth: false,
      },
      additional: {
        countrySelector: 'auto',
        showExamples: true,
        showPrivacyAgreement: false,
      },
    },

    config: {
      ...rawConfig,
    },

    iframeCode: '',
  };

  componentDidMount() {
    const { settings, intl } = this.props;

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        button: {
          ...prevState.settings.button,
          text: intl.formatMessage({
            id: 'iframe_constructor.button.defaultLabel',
            defaultMessage: 'Checkout',
          }),
        },
      },
      config: {
        ...prevState.config,
        button: {
          ...prevState.config.button,
          _DEFAULT_LABEL: intl.formatMessage({
            id: 'iframe_constructor.button.defaultLabel',
            defaultMessage: 'Checkout',
          }),
        },
      },
    }));

    if (settings) {
      const parsedSettings = JSON.parse(settings);
      if (Object.keys(parsedSettings).length > 1) {
        this.setState(prevState => ({
          settings: parsedSettings,
          config: {
            ...prevState.config,
            button: {
              ...prevState.config.button,
              _DEFAULT_LABEL: intl.formatMessage({
                id: 'iframe_constructor.button.defaultLabel',
                defaultMessage: 'Checkout',
              }),
            },
          },
        }));
      }
    }
  }

  componentDidUpdate() {
    const iframeCode = this.generateIFrameCode();

    if (this.state.iframeCode !== iframeCode) {
      this.setState(() => ({
        iframeCode: this.generateIFrameCode(),
      }));
    }
  }

  handleFontChange = e => {
    const { config } = this.state;

    let { name, value } = e.target;

    if (name === 'size') {
      value = Math.max(
        Number(config.font._MIN_SIZE),
        Math.min(Number(config.font._MAX_SIZE), Number(value)),
      );
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        font: {
          ...prevState.settings.font,
          [name]: value,
        },
      },
    }));
  };

  handleFontFamilyChange = ({ value }) => {
    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        font: {
          ...prevState.settings.font,
          family: value,
        },
      },
    }));
  };

  handleFormChange = (e, params) => {
    const { config } = this.state;

    let name, value;

    if (params && params.type === 'range') {
      value = e;
      name = params.name;
    } else if (e.hex) {
      value = e.hex;
      name = params;
    } else {
      name = e.target.name;
      value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

      if (name === 'maxWidth') {
        value = formatNumberValue(value, config.form._MIN_WIDTH, config.form._MAX_WIDTH);
      }
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        form: {
          ...prevState.settings.form,
          [name]: value,
        },
      },
    }));
  };

  handleFieldChange = (e, params) => {
    const { config } = this.state;

    let name, value;
    if (params && params.type === 'range') {
      value = e;
      name = params.name;
    } else if (e.hex) {
      value = e.hex;
      name = params;
    } else {
      name = e.target.name;
      value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

      if (name === 'height') {
        value = formatNumberValue(value, config.field._MIN_HEIGHT, config.field._MAX_HEIGHT);
      } else if (name === 'maxWidth') {
        value = formatNumberValue(value, config.field._MIN_WIDTH, config.field._MAX_WIDTH);
      }
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        field: {
          ...prevState.settings.field,
          [name]: value,
        },
      },
    }));
  };

  handleButtonChange = (e, params) => {
    const { config } = this.state;
    let name, value;
    if (params && params.type === 'range') {
      value = e;
      name = params.name;
    } else if (e.hex) {
      value = e.hex;
      name = params;
    } else {
      name = e.target.name;
      value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

      if (name === 'height') {
        value = formatNumberValue(value, config.button._MIN_HEIGHT, config.button._MAX_HEIGHT);
      } else if (name === 'maxWidth') {
        value = formatNumberValue(value, config.button._MIN_WIDTH, config.button._MAX_WIDTH);
      } else if (name === 'borderRadius') {
        value = formatNumberValue(
          value,
          config.button._MIN_BORDER_RADIUS,
          config.button._MAX_BORDER_RADIUS,
        );
      } else if (name === 'textSize') {
        value = formatNumberValue(
          value,
          config.button._MIN_FONT_SIZE,
          config.button._MAX_FONT_SIZE,
        );
      }
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        button: {
          ...prevState.settings.button,
          [name]: value,
        },
      },
    }));
  };

  handleAdditionalSettingsChange = (e, params) => {
    let name, value;
    if (params && params.type === 'range') {
      value = e;
      name = params.name;
    } else if (e.hex) {
      value = e.hex;
      name = params;
    } else {
      name = e.target.name;
      value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        additional: {
          ...prevState.settings.additional,
          [name]: value,
        },
      },
    }));
  };

  handleSelectorChange = ({ value }) => {
    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        additional: {
          ...prevState.settings.additional,
          countrySelector: value,
        },
      },
    }));
  };

  validateFormColor = (type, fallbackColor) => {
    let color = getFormattedColor(this.state.settings.form[type], fallbackColor);
    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        form: {
          ...prevState.settings.form,
          [type]: color,
        },
      },
    }));
  };

  validateFieldColor = (type, fallbackColor) => {
    let color = getFormattedColor(this.state.settings.field[type], fallbackColor);
    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        field: {
          ...prevState.settings.field,
          [type]: color,
        },
      },
    }));
  };

  validateButtonColor = (type, fallbackColor) => {
    let color = getFormattedColor(this.state.settings.button[type], fallbackColor);
    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        button: {
          ...prevState.settings.button,
          [type]: color,
        },
      },
    }));
  };

  checkButtonLabel = () => {
    const { config } = this.state;
    if (this.state.settings.button.text.length === 0) {
      this.setState(prevState => ({
        settings: {
          ...prevState.settings,
          button: {
            ...prevState.settings.button,
            text: config.button._DEFAULT_LABEL,
          },
        },
      }));
    }
  };

  generateIFrameCode() {
    const { maxWidth, enabledMaxWidth } = this.state.settings.form;
    const { link } = this.props;

    const width = enabledMaxWidth ? `${maxWidth}` : '100%';
    var height = document.getElementById('iframe-preview').offsetHeight;
    
    // Внимание: костыль
    const formattedLink = link.replace('http:', '');
    
    return link
      ? `<iframe src="${formattedLink}" width="${width}" height="${height}" frameborder="0"></iframe>`
      : '';
  }

  render() {
    const { settings, config, iframeCode } = this.state;
    return (
      <Row>
        <Col lg="6">
          <IframeConstructorCommonControls
            handleFontChange={this.handleFontChange}
            handleFontFamilyChange={this.handleFontFamilyChange}
            handleFormChange={this.handleFormChange}
            validateFormColor={this.validateFormColor}
            settings={settings}
            config={config}
          />

          <IframeConstructorFieldsControls
            handleFieldChange={this.handleFieldChange}
            validateFieldColor={this.validateFieldColor}
            settings={settings}
            config={config}
          />

          <IframeConstructorButtonControls
            handleButtonChange={this.handleButtonChange}
            validateButtonColor={this.validateButtonColor}
            checkButtonLabel={this.checkButtonLabel}
            settings={settings}
            config={config}
          />

          <IframeConstructorControlsAdditional
            handleAdditionalSettingsChange={this.handleAdditionalSettingsChange}
            handleSelectorChange={this.handleSelectorChange}
            settings={settings}
          />
        </Col>
        <Col lg="6">
          <IframeConstructorPreview config={settings} />
          <IframeConstructorCode code={iframeCode} />
        </Col>
      </Row>
    );
  }
}

export default injectIntl(IframeConstructor, { withRef: true });
